import { memo, useEffect, useRef, useState } from 'react'

import { Alert, Box, Button, Paper, Stack, Switch, Typography } from '@mui/material'
import { SmepSimpleEventConfig } from '@nativewaves/nw-cloud-sdk/smep'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator } from '@shared/layouts'
import { useEventUpdateMutation } from 'hooks/mutations/smep'
import { PageError } from 'layouts'
import { eventSimpleConfigRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { eventQueryKeys } from 'services/queryKeys'

/**
 * What a first declaration has to look like. The API keeps no reduction of an arbitrary config, so an
 * event that was never declared this way reads back null and there is nothing to prefill from.
 */
const emptySimpleConfig: SmepSimpleEventConfig = {
  name: '',
  views: [],
  globalAudios: [],
  // Typed as a Set because the API models it as a HashSet, but the wire format is a plain array and
  // the generated client does not deserialize into one - so an array is what the editor has to show,
  // and what it gets back. Same cast the full config editor makes for the same field.
  tags: [] as unknown as SmepSimpleEventConfig['tags'],
  fixtureId: '',
}

const SimpleConfigRaw = () => {
  const { t } = useTranslation(['domain'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  // Opting in is local and writes nothing on its own: the declaration only exists once it is saved.
  // Held as the event it was given for rather than as a flag: the router reuses this component when
  // only eventId changes, so a flag would carry one event's opt-in over to the next one.
  const [declaringEventId, setDeclaringEventId] = useState<string>()

  const { eventId } = eventSimpleConfigRoute.useParams()

  const eventQuery = useQuery({
    ...eventQueryKeys.detail({ eventId }),
  })

  const { mutate, isPending } = useEventUpdateMutation()

  const simpleConfig = eventQuery.data?.simpleConfig

  const isDeclaring = declaringEventId === eventId

  useEffect(() => {
    if (editorInstanceRef.current && eventQuery.isSuccess) {
      editorInstanceRef.current.getModel()?.setValue(window.JSON.stringify(simpleConfig ?? emptySimpleConfig, null, 4))
    }
  }, [eventQuery.isSuccess, simpleConfig])

  if (eventQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!eventQuery.isSuccess) {
    return <PageError />
  }

  // Phrased as what the operator wants rather than as the field: the API stores the negation, the
  // same way nonBillable is stored behind a "billable" switch.
  const isApplied = !eventQuery.data.disableSimpleConfig

  const handleToggleApplied = (applied: boolean) =>
    mutate(
      {
        eventId: eventId,
        smepEventEdit: { disableSimpleConfig: !applied },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(
            t(
              applied
                ? 'domain:Workspace.Event.SimpleConfig.applyTurnedOn'
                : 'domain:Workspace.Event.SimpleConfig.applyTurnedOff',
            ),
            { variant: 'success' },
          )
        },
      },
    )

  return (
    <Stack spacing={1} sx={{ height: '100%' }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={[!isApplied && { color: 'action.disabled' }]}>
            <Typography variant="heading-base">{t('domain:Workspace.Event.SimpleConfig.applied')}</Typography>
            <Typography variant="text">{t('domain:Workspace.Event.SimpleConfig.appliedDescription')}</Typography>
          </Box>
          <Switch checked={isApplied} disabled={isPending} onChange={(_, value) => handleToggleApplied(value)} />
        </Box>
      </Paper>

      {!isApplied && <Alert severity="warning">{t('domain:Workspace.Event.SimpleConfig.disabledWarning')}</Alert>}

      {/*
       * An event nobody declared this way is not the same thing as one declared empty, and the editor
       * cannot show the difference - an empty declaration saved by accident would drop every camera the
       * event has. So it stays behind a deliberate step.
       */}
      {!simpleConfig && !isDeclaring ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="heading-md" sx={{ mb: 1 }}>
            {t('domain:Workspace.Event.SimpleConfig.notDeclaredTitle')}
          </Typography>
          <Typography variant="text-sm" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
            {t('domain:Workspace.Event.SimpleConfig.notDeclaredDescription')}
          </Typography>
          <Button variant="contained" onClick={() => setDeclaringEventId(eventId)}>
            {t('domain:Workspace.Event.SimpleConfig.declare')}
          </Button>
        </Paper>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Editor
            value={simpleConfig ?? emptySimpleConfig}
            inProgress={isPending}
            onSubmit={(data) => {
              mutate(
                {
                  eventId: eventId,
                  smepEventEdit: { simpleConfig: data },
                },
                {
                  onSuccess: (data) => {
                    enqueueSnackbar(t('Workspace.Event.eventSimpleConfigUpdated', { name: data.name }), {
                      variant: 'success',
                    })
                  },
                },
              )
            }}
            getEditorInstance={(editorInstance: EditorInstance) => void (editorInstanceRef.current = editorInstance)}
          />
        </Box>
      )}
    </Stack>
  )
}

export const SimpleConfig = memo(SimpleConfigRaw)
